import { useState, useCallback, useRef, useEffect } from 'react';

type SpeechStatus = 'idle' | 'speaking' | 'paused';

interface UseSpeechOptions {
  rate?: number;   // 0.1 – 10, default 1
  pitch?: number;  // 0 – 2, default 1
  volume?: number; // 0 – 1, default 1
  lang?: string;   // e.g. 'en-US'
}

interface UseSpeechReturn {
  speak: (text: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  status: SpeechStatus;
  isSupported: boolean;
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  setVoice: (voice: SpeechSynthesisVoice) => void;
  rate: number;
  setRate: (r: number) => void;
}

export function useSpeech(options: UseSpeechOptions = {}): UseSpeechReturn {
  const isSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const [status, setStatus] = useState<SpeechStatus>('idle');
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [rate, setRate] = useState(options.rate ?? 1);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Load available voices (async in Chrome)
  useEffect(() => {
    if (!isSupported) return;

    const load = () => {
      const available = window.speechSynthesis.getVoices();
      setVoices(available);
      if (!selectedVoice && available.length > 0) {
        // Prefer an English voice
        const en = available.find(v => v.lang.startsWith('en'));
        setSelectedVoice(en ?? available[0]);
      }
    };

    load();
    window.speechSynthesis.addEventListener('voiceschanged', load);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', load);
  }, [isSupported]);

  const speak = useCallback(
    (text: string) => {
      if (!isSupported || !text.trim()) return;
      window.speechSynthesis.cancel();

      const u = new SpeechSynthesisUtterance(text);
      u.rate = rate;
      u.pitch = options.pitch ?? 1;
      u.volume = options.volume ?? 1;
      u.lang = options.lang ?? 'en-US';
      if (selectedVoice) u.voice = selectedVoice;

      u.onstart = () => setStatus('speaking');
      u.onpause = () => setStatus('paused');
      u.onresume = () => setStatus('speaking');
      u.onend = () => setStatus('idle');
      u.onerror = () => setStatus('idle');

      utteranceRef.current = u;
      window.speechSynthesis.speak(u);
    },
    [isSupported, selectedVoice, rate, options.pitch, options.volume, options.lang]
  );

  const pause = useCallback(() => {
    if (isSupported) window.speechSynthesis.pause();
  }, [isSupported]);

  const resume = useCallback(() => {
    if (isSupported) window.speechSynthesis.resume();
  }, [isSupported]);

  const stop = useCallback(() => {
    if (isSupported) {
      window.speechSynthesis.cancel();
      setStatus('idle');
    }
  }, [isSupported]);

  // Stop on unmount
  useEffect(() => () => { if (isSupported) window.speechSynthesis.cancel(); }, [isSupported]);

  return {
    speak,
    pause,
    resume,
    stop,
    status,
    isSupported,
    voices,
    selectedVoice,
    setVoice: setSelectedVoice,
    rate,
    setRate,
  };
}
